import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Save, Zap, ChevronDown, ChevronUp, X, CheckCircle, Loader2, Play } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const METHOD_COLORS = {
  GET: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  POST: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PUT: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  PATCH: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
};

const ASSERTION_TYPES = [
  { value: "status_code", label: "Status Code equals" },
  { value: "body_contains", label: "Body contains" },
  { value: "body_equals", label: "Body equals" },
  { value: "json_path", label: "JSON path equals" },
  { value: "header_contains", label: "Header contains" },
  { value: "response_time", label: "Response time ≤ (ms)" },
];

function stepFromAPI(s, idx) {
  const headers = Object.entries(s.headers || {}).map(([key, value]) => ({ key, value }));
  const extract = Object.entries(s.extract || {}).map(([v, path]) => ({ var: v, path }));
  return {
    step_number: s.step_number || idx + 1,
    name: s.name || "",
    method: s.method || "GET",
    url: s.url || "",
    headers,
    body: s.body || "",
    assertions: (s.assertions || []).map(a => ({ ...a, id: a.id || Date.now() + Math.random() })),
    extract,
    expanded: true,
  };
}

export default function EditAPITest() {
  const { testCaseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [variables, setVariables] = useState([]);
  const [defaultHeaders, setDefaultHeaders] = useState([]);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/test-cases/${testCaseId}`, { withCredentials: true });
        const tc = res.data;
        setName(tc.name || "");
        setDescription(tc.description || "");
        const cfg = tc.api_config || {};
        setBaseUrl(cfg.base_url || "");
        const vars = Object.entries(cfg.variables || {})
          .filter(([k]) => k !== "base_url")
          .map(([key, value]) => ({ key, value }));
        setVariables(vars.length ? vars : [{ key: "", value: "" }]);
        const dh = Object.entries(cfg.default_headers || {}).map(([key, value]) => ({ key, value }));
        setDefaultHeaders(dh.length ? dh : [{ key: "Content-Type", value: "application/json" }]);
        setSteps((tc.api_steps || []).map(stepFromAPI));
      } catch {
        toast.error("Failed to load test");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [testCaseId, navigate]);

  // ── Helpers (same as Create) ────────────────────────────────────────────
  const updateStep = (idx, field, value) =>
    setSteps(prev => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  const addStep = () =>
    setSteps(prev => [...prev, { step_number: prev.length + 1, name: "", method: "GET", url: "", headers: [], body: "", assertions: [], extract: [], expanded: true }]);
  const removeStep = (idx) =>
    setSteps(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })));
  const toggleExpand = (idx) => updateStep(idx, "expanded", !steps[idx].expanded);
  const addStepHeader = (idx) => updateStep(idx, "headers", [...steps[idx].headers, { key: "", value: "" }]);
  const updateStepHeader = (si, hi, f, v) => updateStep(si, "headers", steps[si].headers.map((h, i) => i === hi ? { ...h, [f]: v } : h));
  const removeStepHeader = (si, hi) => updateStep(si, "headers", steps[si].headers.filter((_, i) => i !== hi));
  const addAssertion = (idx) => updateStep(idx, "assertions", [...steps[idx].assertions, { id: Date.now(), type: "status_code", target: "", expected: "200" }]);
  const updateAssertion = (si, ai, f, v) => updateStep(si, "assertions", steps[si].assertions.map((a, i) => i === ai ? { ...a, [f]: v } : a));
  const removeAssertion = (si, ai) => updateStep(si, "assertions", steps[si].assertions.filter((_, i) => i !== ai));
  const addExtract = (idx) => updateStep(idx, "extract", [...steps[idx].extract, { var: "", path: "" }]);
  const updateExtract = (si, ei, f, v) => updateStep(si, "extract", steps[si].extract.map((e, i) => i === ei ? { ...e, [f]: v } : e));
  const removeExtract = (si, ei) => updateStep(si, "extract", steps[si].extract.filter((_, i) => i !== ei));
  const updateVariable = (idx, f, v) => setVariables(prev => prev.map((x, i) => i === idx ? { ...x, [f]: v } : x));
  const updateDefaultHeader = (idx, f, v) => setDefaultHeaders(prev => prev.map((x, i) => i === idx ? { ...x, [f]: v } : x));

  const buildPayload = () => {
    const varsObj = {};
    variables.filter(v => v.key.trim()).forEach(v => { varsObj[v.key.trim()] = v.value; });
    if (baseUrl.trim()) varsObj["base_url"] = baseUrl.trim();
    const defHdrs = {};
    defaultHeaders.filter(h => h.key.trim()).forEach(h => { defHdrs[h.key.trim()] = h.value; });
    const apiSteps = steps.map(s => {
      const headers = {};
      s.headers.filter(h => h.key.trim()).forEach(h => { headers[h.key.trim()] = h.value; });
      const extract = {};
      s.extract.filter(e => e.var.trim() && e.path.trim()).forEach(e => { extract[e.var.trim()] = e.path.trim(); });
      return {
        step_number: s.step_number,
        name: s.name || `Step ${s.step_number}`,
        method: s.method,
        url: s.url,
        headers,
        body: s.body || null,
        assertions: s.assertions.map(a => ({ type: a.type, target: a.target || null, expected: a.expected })),
        extract,
      };
    });
    return { name, description, test_type: "api", api_config: { base_url: baseUrl, variables: varsObj, default_headers: defHdrs }, api_steps: apiSteps };
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Test name is required"); return; }
    if (steps.some(s => !s.url.trim())) { toast.error("All steps need a URL"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/test-cases/${testCaseId}`, buildPayload(), { withCredentials: true });
      toast.success("API test saved!");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/executions?test_case_id=${testCaseId}`, {}, { withCredentials: true });
      toast.success("Running API test...");
      navigate(`/api-execution/${res.data.id}`);
    } catch { toast.error("Failed to start execution"); }
    finally { setRunning(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <AppNav />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Edit API Test</h1>
                <p className="text-slate-400 text-sm">{name}</p>
              </div>
            </div>
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold rounded-sm"
              onClick={handleRun} disabled={running}>
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Run Test
            </Button>
          </div>

          {/* Basic Info */}
          <Card className="glass-card border-white/10 mb-6">
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-slate-300 mb-1 block">Test Name *</Label>
                <Input placeholder="e.g. Login API Flow" className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm"
                  value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <Label className="text-slate-300 mb-1 block">Description</Label>
                <Textarea placeholder="What does this API test cover?" className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm resize-none"
                  rows={2} value={description} onChange={e => setDescription(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Base Configuration */}
          <Card className="glass-card border-white/10 mb-6">
            <CardHeader className="pb-3"><CardTitle className="text-base text-slate-200">Base Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-slate-400 text-xs mb-1 block">Base URL</Label>
                <Input placeholder="https://api.myapp.com" className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm font-mono text-sm"
                  value={baseUrl} onChange={e => setBaseUrl(e.target.value)} />
              </div>
              <div>
                <Label className="text-slate-400 text-xs mb-2 block">Variables</Label>
                <div className="space-y-2">
                  {variables.map((v, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input placeholder="variable_name" className="bg-black/40 border-white/10 text-white rounded-sm text-sm font-mono flex-1"
                        value={v.key} onChange={e => updateVariable(idx, "key", e.target.value)} />
                      <span className="text-slate-500 self-center">=</span>
                      <Input placeholder="default value" className="bg-black/40 border-white/10 text-white rounded-sm text-sm flex-1"
                        value={v.value} onChange={e => updateVariable(idx, "value", e.target.value)} />
                      <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400"
                        onClick={() => setVariables(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 text-xs"
                    onClick={() => setVariables(prev => [...prev, { key: "", value: "" }])}>
                    <Plus className="w-3 h-3 mr-1" /> Add Variable
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-slate-400 text-xs mb-2 block">Default Headers</Label>
                <div className="space-y-2">
                  {defaultHeaders.map((h, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input placeholder="Header-Name" className="bg-black/40 border-white/10 text-white rounded-sm text-sm font-mono flex-1"
                        value={h.key} onChange={e => updateDefaultHeader(idx, "key", e.target.value)} />
                      <span className="text-slate-500 self-center">:</span>
                      <Input placeholder="value" className="bg-black/40 border-white/10 text-white rounded-sm text-sm flex-1"
                        value={h.value} onChange={e => updateDefaultHeader(idx, "value", e.target.value)} />
                      <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400"
                        onClick={() => setDefaultHeaders(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 text-xs"
                    onClick={() => setDefaultHeaders(prev => [...prev, { key: "", value: "" }])}>
                    <Plus className="w-3 h-3 mr-1" /> Add Header
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Steps */}
          <div className="space-y-4 mb-6">
            <h2 className="text-lg font-semibold text-slate-200">Request Steps</h2>
            {steps.map((step, idx) => (
              <Card key={step.step_number} className="glass-card border-white/10">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 cursor-pointer" onClick={() => toggleExpand(idx)}>
                  <div className="w-7 h-7 bg-blue-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold text-sm">{step.step_number}</span>
                  </div>
                  <Badge className={`text-xs font-mono border ${METHOD_COLORS[step.method] || METHOD_COLORS.GET}`}>{step.method}</Badge>
                  <span className="text-slate-300 text-sm flex-1 truncate font-mono">{step.url || <span className="text-slate-600">No URL yet</span>}</span>
                  <div className="flex items-center gap-2">
                    {step.assertions.length > 0 && (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />{step.assertions.length}
                      </Badge>
                    )}
                    {steps.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-7 h-7"
                        onClick={e => { e.stopPropagation(); removeStep(idx); }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    {step.expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </div>
                {step.expanded && (
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-12">
                        <Label className="text-slate-400 text-xs mb-1 block">Step Name</Label>
                        <Input placeholder="e.g. Login and get token" className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm"
                          value={step.name} onChange={e => updateStep(idx, "name", e.target.value)} />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-slate-400 text-xs mb-1 block">Method</Label>
                        <Select value={step.method} onValueChange={v => updateStep(idx, "method", v)}>
                          <SelectTrigger className="bg-black/40 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/10">
                            {HTTP_METHODS.map(m => <SelectItem key={m} value={m} className="text-white hover:bg-white/5">{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-9">
                        <Label className="text-slate-400 text-xs mb-1 block">URL</Label>
                        <Input placeholder="{{base_url}}/api/endpoint" className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm font-mono"
                          value={step.url} onChange={e => updateStep(idx, "url", e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs mb-2 block">Headers</Label>
                      <div className="space-y-2">
                        {step.headers.map((h, hi) => (
                          <div key={hi} className="flex gap-2">
                            <Input placeholder="Header" className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono flex-1"
                              value={h.key} onChange={e => updateStepHeader(idx, hi, "key", e.target.value)} />
                            <span className="text-slate-500 self-center text-xs">:</span>
                            <Input placeholder="value" className="bg-black/40 border-white/10 text-white rounded-sm text-xs flex-1"
                              value={h.value} onChange={e => updateStepHeader(idx, hi, "value", e.target.value)} />
                            <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-7 h-7"
                              onClick={() => removeStepHeader(idx, hi)}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs"
                          onClick={() => addStepHeader(idx)}><Plus className="w-3 h-3 mr-1" /> Add Header</Button>
                      </div>
                    </div>
                    {["POST", "PUT", "PATCH"].includes(step.method) && (
                      <div>
                        <Label className="text-slate-400 text-xs mb-1 block">Request Body (JSON)</Label>
                        <Textarea placeholder={'{\n  "key": "value"\n}'} className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-xs font-mono resize-none"
                          rows={4} value={step.body} onChange={e => updateStep(idx, "body", e.target.value)} />
                      </div>
                    )}
                    <div>
                      <Label className="text-slate-400 text-xs mb-2 block">Assertions</Label>
                      <div className="space-y-2">
                        {step.assertions.map((a, ai) => (
                          <div key={a.id} className="flex gap-2 items-center bg-black/20 rounded-sm p-2 border border-white/5">
                            <Select value={a.type} onValueChange={v => updateAssertion(idx, ai, "type", v)}>
                              <SelectTrigger className="bg-black/40 border-white/10 text-white rounded-sm text-xs w-48 flex-shrink-0"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-slate-900 border-white/10">
                                {ASSERTION_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/5 text-xs">{t.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {(a.type === "json_path" || a.type === "header_contains") && (
                              <Input placeholder={a.type === "json_path" ? "data.id" : "Authorization"}
                                className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono w-32 flex-shrink-0"
                                value={a.target} onChange={e => updateAssertion(idx, ai, "target", e.target.value)} />
                            )}
                            <span className="text-slate-500 text-xs">→</span>
                            <Input placeholder="expected" className="bg-black/40 border-white/10 text-white rounded-sm text-xs flex-1"
                              value={a.expected} onChange={e => updateAssertion(idx, ai, "expected", e.target.value)} />
                            <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-6 h-6"
                              onClick={() => removeAssertion(idx, ai)}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300 text-xs"
                          onClick={() => addAssertion(idx)}><Plus className="w-3 h-3 mr-1" /> Add Assertion</Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs mb-2 block">Extract from Response</Label>
                      <div className="space-y-2">
                        {step.extract.map((e, ei) => (
                          <div key={ei} className="flex gap-2">
                            <Input placeholder="var_name" className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono flex-1"
                              value={e.var} onChange={ev => updateExtract(idx, ei, "var", ev.target.value)} />
                            <span className="text-slate-500 text-xs self-center">←</span>
                            <Input placeholder="json.path" className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono flex-1"
                              value={e.path} onChange={ev => updateExtract(idx, ei, "path", ev.target.value)} />
                            <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-6 h-6"
                              onClick={() => removeExtract(idx, ei)}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 text-xs"
                          onClick={() => addExtract(idx)}><Plus className="w-3 h-3 mr-1" /> Extract Variable</Button>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
            <Button type="button" variant="outline"
              className="w-full border-dashed border-white/20 text-slate-400 hover:text-white hover:border-blue-400 hover:bg-blue-400/5 rounded-sm"
              onClick={addStep}><Plus className="w-4 h-4 mr-2" /> Add Request Step</Button>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" className="text-slate-400" onClick={() => navigate("/dashboard")}>Cancel</Button>
            <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm font-semibold" onClick={handleSave} disabled={saving}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
